import React, { useEffect, useMemo, useState } from 'react';
import styles from './SingleForm.module.scss';
import Header from 'component/layout/header/Header';
import { useFetch } from 'hooks/useFetch';
import { categoriesEndpoints, formsEndpoints } from 'hooks/endpoints';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import FormInfoCard from 'component/organism/form-responses/form-info-card/FormInfoCard';
import TableComponent from 'component/molecules/Table/TableComponent';
import { headerData } from './factory/headerData';
import Button from 'component/atom/forms/button/Button';
import Loader from 'component/atom/loader/Loader';
import SearchInput from 'component/atom/forms/input/searchInput/SearchInput';
import Select from 'component/atom/forms/select/select/Select';
import { useUserContext } from 'app-context/user/UserContext';

const normalize = (v) =>
  String(v ?? '')
    .toLowerCase()
    .trim();
const getByPath = (obj, path) =>
  path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj);

const SingleForm = () => {
  const { formId, botId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const { selectedBot } = useUserContext();

  const [botType, setBotType] = useState(
    () => location.state?.botType ?? (selectedBot?.botType === 1 ? 0 : 2)
  );
  const botTypeOptions =
    selectedBot?.botType === 1
      ? [
          { value: 0, label: 'External Chatbot' },
          { value: 2, label: 'Chat Interface' },
          { value: 1, label: 'Try Me Bot' },
        ]
      : [
          { value: 2, label: 'Chat Interface' },
          { value: 1, label: 'Try Me Bot' },
        ];

  const { data: allCategories } = useFetch(
    ['allCategories', botId],
    categoriesEndpoints.getParentCategories,
    { enabled: true },
    { BotId: botId }
  );

  const {
    data: formResponses,
    refetch,
    isFetching,
  } = useFetch(
    ['forms-responses', formId],
    formsEndpoints.getFormResponseByFormId,
    { enabled: !!formId },
    // { FormId: formId }
    {
      FormId: formId,
      chatSource: botType,
    }
  );

  useEffect(() => {
    if (undefined !== botType) {
      refetch();
    }
  }, [botType]);

  const categoryOptions = useMemo(() => {
    const items = allCategories?.data?.items ?? [];
    const mapped = items.map((c) => ({
      value: c.categoriesId,
      label: c.categoryText,
    }));
    return [{ value: '', label: 'All Departments' }, ...mapped];
  }, [allCategories]);

  const categoryFiltered = useMemo(() => {
    const items = formResponses?.data?.result?.items ?? [];
    if (!selectedCategoryId) return items;
    return items.filter((i) => i.parentCategoryId === selectedCategoryId);
  }, [formResponses, selectedCategoryId]);

  const searchableKeys = useMemo(
    () => (headerData?.fields ?? []).map((f) => f.fieldName).filter(Boolean),
    []
  );

  const matchesQuery = useMemo(() => {
    return (row, rawQuery) => {
      const q = normalize(rawQuery);
      if (!q) return true;
      const tokens = q.split(/\s+/).filter(Boolean);
      return tokens.every((t) =>
        searchableKeys.some((key) => {
          const val = key.includes('.') ? getByPath(row, key) : row?.[key];
          return normalize(val).includes(t);
        })
      );
    };
  }, [searchableKeys]);

  const visibleItems = useMemo(() => {
    if (!searchValue) return categoryFiltered;
    return categoryFiltered.filter((row) => matchesQuery(row, searchValue));
  }, [categoryFiltered, searchValue, matchesQuery]);

  useEffect(() => {
    navigate('.', {
      replace: true,
      state: { ...(location.state || {}), botType },
    });
  }, [botType]);

  const renderDetailsButton = (item) => {
    return (
      <Button
        label='View Details'
        onClick={(e) => {
          e.stopPropagation();
          navigate(
            `/bot/form-leads/${formId}/${item?.item?.categoriesId}/${botId}`,
            {
              state: {
                botType,
              },
            }
          );
        }}
        variant='primary-rev'
        className={styles['view-details-btn']}
      />
    );
  };

  const onInternalSearch = (value) => setSearchValue(value ?? '');
  const handleSearchChange = (_name, value) => onInternalSearch(value);

  return (
    <div>
      <Header
        title='Form Response'
        isBack
        isSub
        titleClassName={styles['title']}
        children={
          <Select
            placeholder='Type'
            selectClassname={styles['select-type']}
            classname={styles['select-container']}
            inputClassName={styles['select-input']}
            value={botType}
            onChange={(e) => setBotType(e.target.value)}
            options={botTypeOptions}
          />
        }
        containerClassname={styles['header-container']}
      />
      <div className={styles['form-responses-container']}>
        <div className={styles['form-information-container']}>
          <FormInfoCard
            title='Total Leads'
            total={formResponses?.data?.result?.totalLeads[0]?.totalCount}
            icon='total-leads'
          />
          <FormInfoCard
            title='High Intent Leads'
            total={
              formResponses?.data?.result?.activeConversations[0]?.totalCount
            }
            subTitle='Intent Score 4-5'
            icon='high-intent-leads'
          />
          <FormInfoCard
            title='Files Submitted'
            total={formResponses?.data?.result?.filesSubmitted[0]?.totalCount}
            subTitle='Documents Received'
            icon='files-submitted'
          />
        </div>

        {isFetching && <Loader isComponent />}

        {!isFetching && (
          <div className={styles['table-wrapper']}>
            <div className={styles['table-header']}>
              <span className={styles['title']}>Degree Programs</span>
              <div className={styles['filter-items']}>
                <SearchInput
                  onChange={handleSearchChange}
                  value={searchValue}
                  placeholder='Search'
                  classname={styles['search']}
                />
                <Select
                  options={categoryOptions}
                  value={selectedCategoryId}
                  placeholder='Select Department'
                  onChange={(e) => setSelectedCategoryId(e.target.value || '')}
                  classname={styles['select']}
                />
              </div>
            </div>
            <TableComponent
              rowKey='categoriesId'
              headerData={headerData}
              externalData={visibleItems}
              renderAdditionalComponent={renderDetailsButton}
              hasAction={true}
              // onInternalSearch={onInternalSearch}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SingleForm;
